import React, {Component} from "react";
import {APIClient} from "../../utils/APIClient";
import {API_URL} from "../../settings/local_settings";
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import {ClientHeader} from "./ClientHeader";
import {ClientTabs} from "./ClientTabs";
import {ClientIntegrationsInfo} from "./ClientIntegrationsInfo";
import {ClientProfileForm} from "./ClientProfileForm";
import {Spinner} from "../Spinner";
import {FaIcon} from "../FaIcon";
import {withTranslation} from "react-i18next";
import {AgencyFormFieldError} from "../agency/AgencyFormFieldError";
import {InviteUserModal} from "../invitations/InviteUserModal";

function getUserLabel(user) {
    return user?.name || user?.full_name || user?.email || user?.username || '';
}

function getUserMeta(user) {
    if (user?.email && user.email !== getUserLabel(user)) return user.email;
    return user?.username || '';
}

class ClientSettingsUsersPanelClass extends Component {
    constructor(props) {
        super(props);

        this.state = {
            users: null,
            selectedUserIds: null,
            savingChanges: false,
            hasChanges: false,
            userCanAssignClients: null,
            searchText: ''
        };

        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleSelectAll = this.handleSelectAll.bind(this);
        this.handleUnselectAll = this.handleUnselectAll.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleNewUserInvited = this.handleNewUserInvited.bind(this);
    }

    componentDidMount() {
        this.getUsersOfClient();
    }

    userCanChangeTeam() {
        return this.state.userCanAssignClients;
    }

    dashboUsersURL() {
        return `${API_URL}client/${this.props.clientId}/dashbo-users`;
    }

    getUsersOfClient() {
        new APIClient(this.dashboUsersURL()).call('GET').then(response => {
            if (response.status === 'OK') {
                this.setState({
                    users: response.data.all_users,
                    selectedUserIds: (response.data.client_user_ids || []).map(userId => userId.toString()),
                    userCanAssignClients: response.data.user_can_assign_clients
                });
            } else {
                alert(response['error_message']);
            }
        });
    }

    handleNewUserInvited() {
        this.getUsersOfClient();
    }

    handleSearchChange(event) {
        this.setState({searchText: event.target.value});
    }

    getFilteredUsers() {
        const users = this.state.users || [];
        const searchText = this.state.searchText.trim().toLowerCase();

        if (!searchText) return users;

        return users.filter(user => {
            const searchableText = [
                getUserLabel(user),
                getUserMeta(user),
                user?.email,
                user?.username
            ].filter(Boolean).join(' ').toLowerCase();

            return searchableText.includes(searchText);
        });
    }

    isUserSelected(userId) {
        return (this.state.selectedUserIds || []).includes(userId.toString());
    }

    handleToggleUser(userId) {
        if (!this.userCanChangeTeam()) return;

        const userIdAsString = userId.toString();
        const selectedUserIds = this.isUserSelected(userIdAsString)
            ? this.state.selectedUserIds.filter(selectedId => selectedId !== userIdAsString)
            : this.state.selectedUserIds.concat([userIdAsString]);

        this.setState({
            hasChanges: true,
            selectedUserIds: selectedUserIds
        });
    }

    handleSelectAll(event) {
        event.preventDefault();
        if (!this.userCanChangeTeam()) return;

        this.setState({
            hasChanges: true,
            selectedUserIds: this.getFilteredUsers().map(user => user.id.toString())
        });
    }

    handleUnselectAll(event) {
        event.preventDefault();
        if (!this.userCanChangeTeam()) return;

        this.setState({
            hasChanges: true,
            selectedUserIds: []
        });
    }

    handleSubmit() {
        this.setState({
            savingChanges: true
        });

        new APIClient(this.dashboUsersURL()).call('PUT', {
            selected_user_ids: this.state.selectedUserIds
        }).then(response => {
            if (response.status === 'OK') {
                this.setState({
                    hasChanges: false
                });
            } else {
                alert(response['error_message']);
            }

            this.setState({
                savingChanges: false
            });
        });
    }

    renderUsersList() {
        const {t} = this.props;

        if (this.state.users !== null && this.state.selectedUserIds !== null) {
            const filteredUsers = this.getFilteredUsers();
            const selectedCount = this.state.selectedUserIds.length;

            return (
                <>
                    <div className="client-settings-users-toolbar">
                        <input
                            type="text"
                            value={this.state.searchText}
                            onChange={this.handleSearchChange}
                            placeholder={t('client_settings.users_search_placeholder')}
                        />
                        <span>{t('client_settings.users_access_count', {count: selectedCount})}</span>
                    </div>
                    <div className="client-settings-users-actions">
                        <button type="button" onClick={this.handleSelectAll} disabled={!this.userCanChangeTeam()}>
                            {t('multiselect.select_all')}
                        </button>
                        <button type="button" onClick={this.handleUnselectAll} disabled={!this.userCanChangeTeam()}>
                            {t('multiselect.remove_selection')}
                        </button>
                    </div>
                    {filteredUsers.length > 0 ? (
                        <div className="client-settings-users-list">
                            {filteredUsers.map(user => {
                                const isSelected = this.isUserSelected(user.id);
                                return (
                                    <button
                                        type="button"
                                        key={user.id}
                                        className={`client-settings-user-row${isSelected ? ' -selected' : ''}`}
                                        onClick={() => this.handleToggleUser(user.id)}
                                        disabled={!this.userCanChangeTeam()}
                                    >
                                        <span className="client-settings-user-avatar">
                                            <FaIcon iconName="user"/>
                                        </span>
                                        <span className="client-settings-user-copy">
                                            <strong>{getUserLabel(user)}</strong>
                                            {getUserMeta(user) && <small>{getUserMeta(user)}</small>}
                                        </span>
                                        <span className="client-settings-user-access-status">
                                            {isSelected ? t('client_settings.user_has_access') : t('client_settings.user_without_access')}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="client-settings-users-empty">
                            <FaIcon iconName="users"/>
                            <p>{t('client_settings.users_empty')}</p>
                        </div>
                    )}
                </>
            );
        }

        return <p>{t('common.loading')}</p>;
    }

    renderSaveButton() {
        if (!this.userCanChangeTeam()) return null;

        if (this.state.savingChanges) {
            return <Spinner color="positive small"/>;
        }

        return (
            <button
                type="button"
                className="btn -alt"
                onClick={this.handleSubmit}
                disabled={!this.state.hasChanges}
            >
                {this.props.t('client_settings.save_users')}
            </button>
        );
    }

    render() {
        const {t} = this.props;

        return (
            <div className="box client-settings-card client-settings-users-card">
                <header className="client-settings-card-header client-settings-users-header">
                    <div className="client-settings-users-heading">
                        <span className="client-settings-card-icon">
                            <FaIcon iconName="users"/>
                        </span>
                        <span>
                            <h2 className="box-title">{t('client_settings.users_title')}</h2>
                            <p>{t('client_settings.users_description')}</p>
                        </span>
                    </div>
                    <InviteUserModal
                        onUserInvited={this.handleNewUserInvited}
                        initialSelectedClientIds={[this.props.clientId.toString()]}
                        buttonText={t('client_settings.new_user')}
                        iconOnly={true}
                    />
                </header>
                <div className="client-settings-card-body">
                    {this.renderUsersList()}
                </div>
                <div className="client-settings-card-footer">
                    {this.renderSaveButton()}
                </div>
            </div>
        );
    }
}

const ClientSettingsUsersPanel = withTranslation()(ClientSettingsUsersPanelClass);

class ClientConfigurationContentClass extends Component {
    constructor(props) {
        super(props);

        this.state = {
            name: '',
            draftName: '',
            nameErrors: {},
            nameModalOpen: false,
            savingName: false,
        };

        this.handleOpenNameModal = this.handleOpenNameModal.bind(this);
        this.handleCloseNameModal = this.handleCloseNameModal.bind(this);
        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleSubmitName = this.handleSubmitName.bind(this);
    }

    componentDidMount() {
        this.updateClientData();
    }

    updateClientData() {
        new APIClient(this.updateClientDataURL()).call('GET').then((data) => {
            if (data.status === 'ERROR') {
                alert(data['error_message']);
            } else if (data.status === 'OK') {
                this.setState({
                    name: data.data['name'],
                    draftName: data.data['name'],
                });
            }
        });
    }

    updateClientDataURL() {
        return API_URL + 'client/' + this.props.clientId;
    }

    editClientDataURL() {
        return API_URL + 'client/edit/' + this.props.clientId;
    }

    handleOpenNameModal() {
        this.setState({
            draftName: this.state.name,
            nameErrors: {},
            nameModalOpen: true,
        });
    }

    handleCloseNameModal() {
        if (this.state.savingName) return;
        this.setState({nameModalOpen: false});
    }

    handleNameChange(event) {
        this.setState({draftName: event.target.value});
    }

    handleSubmitName() {
        this.setState({savingName: true});
        new APIClient(this.editClientDataURL()).call('POST', {
            name: this.state.draftName
        }).then((data) => {
            let errors = {};
            if (data.status === 'ERROR') {
                errors = JSON.parse(data.errors);
                this.setState({nameErrors: errors});
            } else if (data.status === 'OK') {
                this.setState({
                    name: this.state.draftName,
                    nameErrors: {},
                    nameModalOpen: false,
                });
            }
        }).finally(() => {
            this.setState({savingName: false});
        });
    }

    renderClientNameCard() {
        const {t} = this.props;

        return (
            <div className="box client-settings-name-card">
                <div>
                    <p className="client-settings-kicker">{t('client_settings.kicker')}</p>
                    <h1>{this.state.name || t('common.loading')}</h1>
                    <p>{t('client_settings.description')}</p>
                </div>
                <button
                    type="button"
                    className="client-settings-edit-name-button"
                    onClick={this.handleOpenNameModal}
                    aria-label={t('client_settings.edit_client_name')}
                >
                    <FaIcon iconName="pencil-alt"/>
                </button>
            </div>
        );
    }

    renderEditNameModal() {
        const {t} = this.props;

        return (
            <Modal isOpen={this.state.nameModalOpen} toggle={this.handleCloseNameModal}>
                <ModalHeader toggle={this.handleCloseNameModal}>{t('client_settings.edit_client_name')}</ModalHeader>
                <ModalBody>
                    <div className="modal-section">
                        <div className="form-row">
                            <label><span title={t('client_settings.required_field')}>*</span>{t('client_settings.client_name')}:</label>
                            <input
                                type="text"
                                name="name"
                                value={this.state.draftName}
                                onChange={this.handleNameChange}
                            />
                            <AgencyFormFieldError errors={this.state.nameErrors} fieldName="name"/>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn -negative" onClick={this.handleCloseNameModal}>
                        {t('client_profile.close')}
                    </button>
                    <button type="button" className="btn -alt" onClick={this.handleSubmitName} disabled={this.state.savingName}>
                        {this.state.savingName ? t('common.loading') : t('client_settings.save')}
                    </button>
                </ModalFooter>
            </Modal>
        );
    }

    render() {
        const {t} = this.props;

        return (
            <section className="section-content client-settings-page">
                <ClientHeader name={this.state.name}/>
                <ClientTabs clientId={this.props.clientId}/>

                <div className="section-body client-settings-body">
                    <div className="client-settings-layout">
                        <div className="client-settings-main-column">
                            {this.renderClientNameCard()}

                            <div className="box client-settings-card client-settings-profile-card">
                                <header className="client-settings-card-header">
                                    <span className="client-settings-card-icon">
                                        <FaIcon iconName="user-circle"/>
                                    </span>
                                    <span>
                                        <h2 className="box-title">{t('client_settings.profile_title')}</h2>
                                        <p>{t('client_settings.profile_description')}</p>
                                    </span>
                                </header>
                                <div className="client-settings-card-body">
                                    <ClientProfileForm
                                        clientId={this.props.clientId}
                                        showCancelButton={false}
                                    />
                                </div>
                            </div>

                            <div className="client-settings-embedded-card">
                                <ClientIntegrationsInfo clientId={this.props.clientId} settingsCard={true}/>
                            </div>
                        </div>

                        <aside className="client-settings-users-column">
                            <ClientSettingsUsersPanel clientId={this.props.clientId}/>
                        </aside>
                    </div>
                </div>

                {this.renderEditNameModal()}
            </section>
        )
    }
}

export const ClientConfigurationContent = withTranslation()(ClientConfigurationContentClass);
